import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fundamentalsService } from '../../services/fundamentalsService';
import { useSortableRows, SortableTh } from '../../components/tables/SortableTable';

// Fundamentals review — Folio's Review tab (superuser only; rendered by FolioPage for the company its picker selected).
// It follows the pipeline's own workflow (owner 2026-10-25), top to bottom:
//   Needs you   — questions the pipeline cannot settle (expanded): pick an option / Approve the agreed answer; it is
//                 recorded at once and applied at the NEXT rebuild.
//   Waiting for the answerer — questions the blind Claude answerers have not settled yet (collapsed; you may decide).
//   Settled     — by code / by two agreeing models / by you (collapsed): Looks right / Change…, the audit samples first.
//   then, collapsed: the statements as filed, the answerers' usage, and the HISTORY of how rows continued across
//   filings (renames / splits the code recorded — information, nothing to confirm).
// Presentation only: every decision goes through the same endpoints as before. Active review time is measured here
// (one beat per active minute) for criterion 11. ?q=<id> (a row of the cross-company queue, 2026-10-26) opens the
// section holding that question, scrolls its TOP under the top bar and highlights it; that card shows its question,
// outcome and decision first (its brief and answers follow).

export const KIND = {
  unbound_node: { label: 'New line', cls: 'bg-sky-500/10 text-sky-700 dark:text-sky-300' },
  version_material: { label: 'Changed value', cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-300' },
  sign_flip: { label: 'Sign flip', cls: 'bg-red-500/10 text-red-700 dark:text-red-300' },
  context_suspect: { label: 'Other period’s value', cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-300' },
  split_reason: { label: 'Why a line split', cls: 'bg-purple-500/10 text-purple-700 dark:text-purple-300' },
  rename_check: { label: 'Renamed row', cls: 'bg-teal-500/10 text-teal-700 dark:text-teal-300' },
  decision_orphaned: { label: 'Your decision no longer applies?', cls: 'bg-rose-500/10 text-rose-700 dark:text-rose-300' },
  sign_suspect: { label: 'Tag sign wrong?', cls: 'bg-red-500/10 text-red-700 dark:text-red-300' },
  standard_line: { label: 'Standard line', cls: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300' },
  absent_row: { label: 'Row only the 10-Qs print', cls: 'bg-orange-500/10 text-orange-700 dark:text-orange-300' },
  duplicate_value: { label: 'One fact, two figures', cls: 'bg-red-500/10 text-red-700 dark:text-red-300' },
};
const EVENT = {
  split: 'Split', rename: 'Renamed', new: 'New line', aggregate: 'Sum of lines', decided: 'Decided in review',
  merged: 'Made of lines', carved: 'Carved out',
};
const FAMILIES = [
  { key: 'IS_CI', label: 'Income' }, { key: 'BS', label: 'Balance sheet' }, { key: 'CF', label: 'Cash flow' },
];
const FAMILY_LABEL = { IS_CI: 'Income', BS: 'Balance sheet', CF: 'Cash flow' };

const NULL_REASON = {
  non_additive: 'not derived — per-share values and share counts do not add up across quarters',
  basis_break: 'not derived — the line was restated between the two inputs',
  missing_input: 'not derived — the 9-month figure was not reported',
  input_null: 'not derived — an input is missing',
  sign_unresolved: 'not derived — an input\'s sign is an open question',
  folded_into: 'folded into another row by a later filing (its figure is kept in the history)',
  ambiguous_chain: 'two lines of this row disagree from the same moment — not picked',
};

// a quarter of a year a later 10-K redefined (#101): what the figure is, in the analyst's words
function fillNote(c, unit) {
  const f = c.fill || {};
  const mid = (iv) => (iv ? (Number(iv[0]) + Number(iv[1])) / 2 : null);
  if (c.kind === 'as_reported_Q') {
    return `as originally reported — FY ${f.fy || ''} was later restated on a new definition; `
      + `reported year ${fmt(mid(f.reported_year), unit)}, restated ${fmt(mid(f.served_year), unit)} `
      + '(the difference is the definition-change row)';
  }
  if (c.kind === 'release_Q') {
    return `the company's own figure, read from its earnings release ${f.release || ''}`
      + (f.recast ? ' — the next year\'s release, recast on the definition of the year we serve' : '')
      + (f.bridge ? ' (the year\'s other quarters were never recast — see the definition-change row)' : '');
  }
  if (c.kind === 'residual_Q4') {
    return 'year − three quarters across a definition change: this quarter carries the whole change';
  }
  if (c.kind === 'derived_Q4_presented') {
    return 'year − nine months, both re-presented by the company on the same recast basis';
  }
  if (c.kind === 'bridge') {
    return `definition change for FY ${f.fy || ''}: the restated year − the four quarters as originally reported`;
  }
  return '';
}

// ONE sign convention with the Folio grid (UI fixes 2026-10-27): the figure as served, a negative in parentheses —
// "(40)", never "−40" — and no green / red on a statement figure (the grid's own rule: a statement figure is not good or
// bad). The review page printed "−40m" beside the grid's "(40)".
function fmt(raw, unit) {
  if (raw == null) return '—';
  const v = Number(raw) === 0 ? 0 : Number(raw);              // never "−0"
  if (v === 0) return '0';
  const a = Math.abs(v);
  const t = unit === 'per_share' ? a.toFixed(2) : (a / 1e6).toLocaleString('en-US', { maximumFractionDigits: 0 });
  return v < 0 && Number(t.replace(/[^0-9.]/g, '')) !== 0 ? `(${t})` : t;
}

// a figure the server already wrote as text ("−1,234", "-5", "1,234"): a leading minus becomes parentheses
const parenText = (t) => (typeof t === 'string' && /^[−-]\s*\d/.test(t) ? `(${t.replace(/^[−-]\s*/, '')})` : t);
// the same notation inside the server's sentences (option labels, checks, the question): "−2m" → "(2)m" — only the
// minus SIGN (U+2212) the server writes before a figure, never a hyphen (10-K, accession numbers, year-to-date)
const pn = (t) => (typeof t === 'string' ? t.replace(/−(\d[\d,]*(?:\.\d+)?)/g, '($1)') : t);

const edgarUrl = (acc) => {
  if (!acc) return null;
  const cik = String(parseInt(acc.slice(0, 10), 10));
  return `https://www.sec.gov/Archives/edgar/data/${cik}/${acc.replace(/-/g, '')}/`;
};

function Tile({ label, value, sub, onClick, valueCls = '' }) {
  const C = onClick ? 'button' : 'div';
  return (
    <C onClick={onClick}
      className={`rounded-xl border border-gray-200 p-3 text-left dark:border-gray-800 ${onClick ? 'hover:border-brand-gold' : ''}`}>
      <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`mt-1 font-serif text-xl tabular-nums text-gray-900 dark:text-gray-100 ${valueCls}`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{sub}</div>}
    </C>
  );
}

function Pills({ items, value, onChange }) {
  return (
    <div className="scrollbar-thin flex gap-2 overflow-x-auto">
      {items.map((it) => (
        <button key={it.key} type="button" onClick={() => onChange(it.key)} aria-pressed={value === it.key}
          className={`min-h-[44px] whitespace-nowrap rounded-full border px-3 py-1 text-xs uppercase tracking-wide focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/70 sm:min-h-0 ${value === it.key
            ? 'border-brand-gold text-brand-gold' : 'border-gray-300 text-gray-600 dark:border-gray-700 dark:text-gray-300'}`}>
          {it.label}
        </button>
      ))}
    </div>
  );
}

// ── Questions ────────────────────────────────────────────────────────────────────────────────────────────────
const STAGE = {
  needs_you: { label: 'Needs you', cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-300' },
  waiting: { label: 'Waiting for answers', cls: 'bg-gray-500/10 text-gray-600 dark:text-gray-300' },
  applied_by_pipeline: { label: 'Applied by the pipeline', cls: 'bg-green-500/10 text-green-700 dark:text-green-300' },
  answered_by_you: { label: 'Answered by you', cls: 'bg-sky-500/10 text-sky-700 dark:text-sky-300' },
};

function optionText(o) {
  if (!o) return '—';
  if (o.choice === 'bind') return <>Same line as <b>{o.label}</b></>;
  if (o.choice === 'none_fits') return <>None of the options — {o.answer}</>;
  return pn(o.label || o.choice);
}

function Sources({ list }) {
  if (!list || !list.length) return null;
  return (
    <div className="mt-1 text-xs text-gray-500">Sources: {list.map((src, i) => (
      <span key={i}>{i > 0 && ' · '}{/^https:\/\/(www\.)?sec\.gov\//.test(src)
        ? <a href={src} target="_blank" rel="noreferrer noopener" className="underline">{src.replace(/^https:\/\/(www\.)?/, '')}</a>
        : src}</span>))}</div>
  );
}

function Evidence({ ev }) {
  if (!ev || typeof ev !== 'object') return <span>(unreadable citation)</span>;
  if (typeof ev.quote === 'string') return <span>¶{String(ev.paragraph)} “{ev.quote}”</span>;
  const str = (x) => (typeof x === 'string' ? x : '');
  const who = str(ev.concept) ? str(ev.concept).split(':').pop() : str(ev.line_id).split('|')[1]?.split(':').pop();
  return (
    <span className="tabular-nums">
      {str(ev.accession) ? <a href={edgarUrl(ev.accession)} target="_blank" rel="noreferrer noopener"
        className="text-brand-gold hover:underline">{ev.accession}</a> : 'line'} · {who} · {str(ev.period)} ·{' '}
      <span>{fmt(ev.value)}</span>m
    </span>
  );
}

function AnswerRow({ a, label }) {
  const ok = a.verification?.status === 'verified';
  const ex = a.explain || {};
  const owner = a.answerer.startsWith('owner:');
  const inv = a.answerer === 'investigator';
  const badge = inv ? (ok ? 'advice — every number checked' : 'advice — a check failed')
    : (ok ? 'counts — every number checked' : 'does not count');
  return (
    <div className={`min-w-0 rounded-lg border p-3 text-sm [overflow-wrap:anywhere] ${inv ? 'border-sky-500/30 bg-sky-500/5' : 'border-gray-100 dark:border-gray-800/70'}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-gray-800 dark:text-gray-200">{owner ? `You (${a.answerer.slice(6)})`
          : inv ? 'Investigator' : (label || 'Answer')}</span>
        {inv && <span className="text-[11px] uppercase tracking-wide text-gray-500"
          title="Read the stored filings and sec.gov with read-only tools; advises you, decides nothing">reads the filings</span>}
        {a.blind && !inv && <span className="text-[11px] uppercase tracking-wide text-gray-500"
          title="It never saw any other answer">blind</span>}
        {!owner && <span className={`rounded-full px-2 py-0.5 text-[11px] ${ok ? 'bg-green-500/10 text-green-700 dark:text-green-300'
          : 'bg-red-500/10 text-red-700 dark:text-red-300'}`}>{badge}</span>}
        <span className="text-xs text-gray-500">{(a.at || '').slice(0, 16).replace('T', ' ')}</span>
      </div>
      <div className="mt-1 text-gray-800 dark:text-gray-200">Chose: {optionText(a.option)}.</div>
      {(ex.problems || []).length > 0 && (
        <div className="mt-1 rounded-md bg-red-500/5 px-2 py-1 text-xs text-red-700 dark:text-red-300">
          Why it does not count: {ex.problems.join(' ')}</div>
      )}
      {a.option?.choice === 'none_fits' && (
        <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
          {a.option.line_id && <div>Continues the line: <span className="font-mono">{a.option.line_id}</span></div>}
          {a.option.why_the_options_fail && <div>Why the options fail: {a.option.why_the_options_fail}</div>}
        </div>
      )}
      {a.reasoning && <div className="mt-1 break-words text-gray-600 [overflow-wrap:anywhere] dark:text-gray-300">{a.reasoning}</div>}
      {inv && <Sources list={a.verification?.sources} />}
      {((a.evidence || []).length > 0 || (a.claims || []).length > 0) && (
        <details className="mt-1 text-xs text-gray-600 dark:text-gray-400">
          <summary className="cursor-pointer select-none">The numbers it quoted ({(a.evidence || []).length}) — each checked against the filings</summary>
          <div className="mt-1 flex flex-col gap-0.5">
            {(a.evidence || []).map((ev, i) => <span key={i}>[{i}] <Evidence ev={ev} /></span>)}
            {(a.claims || []).map((c, i) => (
              <span key={`c${i}`} className="tabular-nums">sum: {c.terms.map(([idx, sg]) => `${sg < 0 ? '−' : '+'}[${idx}]`).join(' ')} = {fmt(c.result)}m</span>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

const CHECK_CLS = { ok: 'text-green-700 dark:text-green-400', conflict: 'text-red-600 dark:text-red-400',
  info: 'text-gray-600 dark:text-gray-300' };
const CHECK_MARK = { ok: '✓', conflict: '✗', info: '•' };

// the answers numbered in the order they arrived ("Answer 1", "Answer 2", …) — an answerer's slot letter is reused across
// rounds, so "claude:A … Same choice as claude:A" read as an answer agreeing with itself (UI fixes 2026-10-27)
const optKey = (o) => (o ? JSON.stringify(o, Object.keys(o).sort()) : '');
function numbered(answers) {
  return answers.slice().sort((x, y) => String(x.at || '').localeCompare(String(y.at || '')) || x.id - y.id)
    .map((a, i) => ({ a, label: `Answer ${i + 1}` }));
}
function joinWords(xs) {
  return xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}
// the agreement, stated ONCE: which answers chose what
function Agreement({ list }) {
  if (list.length < 2) return null;
  const groups = new Map();
  list.forEach(({ a, label }) => {
    const k = optKey(a.option);
    if (!groups.has(k)) groups.set(k, { option: a.option, labels: [] });
    groups.get(k).labels.push(label.replace('Answer ', ''));
  });
  const g = [...groups.values()];
  if (g.length === 1) {
    return <div className="mb-2 text-xs text-gray-600 dark:text-gray-300">All {list.length} answers chose the same option: {optionText(g[0].option)}.</div>;
  }
  return (
    <div className="mb-2 text-xs text-amber-700 dark:text-amber-300">The answers disagree: {g.map((x, i) => (
      <span key={i}>{i > 0 && '; '}{x.labels.length > 1 ? 'Answers' : 'Answer'} {joinWords(x.labels)} chose {optionText(x.option)}</span>))}.</div>
  );
}

function CodeChecks({ checks }) {
  if (!(checks || []).length) return null;
  return (
    <ul className="mt-1 space-y-0.5 text-xs">
      {checks.map((c, i) => (
        <li key={i} className={`flex gap-1.5 ${CHECK_CLS[c.status] || ''}`}><span>{CHECK_MARK[c.status]}</span><span className="min-w-0 [overflow-wrap:anywhere]">{pn(c.text)}</span></li>
      ))}
    </ul>
  );
}

function BriefView({ b }) {
  if (!b) return null;
  const cap = (x) => (x ? x[0].toUpperCase() + x.slice(1) : '');
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500">{cap(b.statement)} · {b.filing.label}
        {b.section && <> · under “{b.section}”</>}</div>
      <div className="mt-1 break-words text-sm text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">{pn(b.question)}</div>
      {(b.table || []).length > 0 && (
        <div className="scrollbar-thin mt-2 overflow-x-auto">
          <table className="text-xs tabular-nums">
            <thead><tr className="text-gray-500">
              <th className="py-1 pr-3 text-left font-normal">Millions</th>
              {b.periods.map((p) => <th key={p.key} className="whitespace-nowrap px-2 py-1 text-right font-normal">{p.label}</th>)}
            </tr></thead>
            <tbody>
              {b.table.map((r, i) => (
                <tr key={i} className={r.is_new ? 'text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-300'}>
                  <td className="max-w-[280px] py-1 pr-3">{r.line}</td>
                  {b.periods.map((p) => <td key={p.key} className="whitespace-nowrap px-2 py-1 text-right">{parenText(r.values[p.key]) || ''}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-2 text-[11px] uppercase tracking-wide text-gray-500">Checked by code</div>
      <CodeChecks checks={b.checks} />
      {(b.excerpts?.excerpts || []).length > 0 && (
        <details className="mt-2 text-xs text-gray-600 dark:text-gray-300">
          <summary className="cursor-pointer select-none text-[11px] uppercase tracking-wide text-gray-500">
            What the filing itself says ({b.excerpts.excerpts.length} short extracts, {b.excerpts.chars} characters)</summary>
          <div className="mt-1 space-y-1.5">
            {b.excerpts.excerpts.map((e) => (
              <blockquote key={e.paragraph} className="border-l-2 border-gray-300 pl-2 dark:border-gray-700">
                <span className="text-gray-500">¶{e.paragraph}</span> <span className="[overflow-wrap:anywhere]">{e.text}</span></blockquote>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function CopyBrief({ text }) {
  const [done, setDone] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard?.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 2000); }); }}
      className="inline-flex min-h-[44px] items-center text-xs text-brand-gold underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/70 sm:min-h-0"
      title="The same brief the Claude answerers get, without their answers — paste it into your own AI">
      {done ? 'Copied' : 'Copy the question for your own AI'}</button>
  );
}

function agreedOption(item) {
  // the latest VERIFIED blind Claude answer per answerer; all the same option → that option (as on the server)
  const latest = {};
  (item.answers || []).forEach((a) => { if (a.blind && a.answerer.startsWith('claude:')) latest[a.answerer] = a; });
  const good = Object.values(latest).filter((a) => a.verification?.status === 'verified');
  if (good.length < 2) return null;
  const keys = new Set(good.map((a) => JSON.stringify(a.option, Object.keys(a.option).sort())));
  if (keys.size !== 1) return null;
  const idx = item.options.findIndex((o) => JSON.stringify(o, Object.keys(o).sort()) === [...keys][0]);
  return idx >= 0 ? idx : null;
}

function Step({ n, title, children }) {
  return (
    <div className="mt-3 border-l-2 border-gray-200 pl-3 dark:border-gray-800">
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{n} · {title}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function OptionPicker({ item, onDone, mode }) {
  const [choice, setChoice] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [all, setAll] = useState(false);
  const opts = item.brief?.options?.length ? item.brief.options
    : item.options.map((o, i) => ({ index: i, title: o.label || o.choice, summary: '', checks: [], rank: 0 }));
  const shown = all ? opts : opts.filter((o) => o.rank < 3);
  const hidden = opts.length - opts.filter((o) => o.rank < 3).length;
  const go = async () => {
    setBusy(true); setErr('');
    try {
      const fn = mode === 'override' ? fundamentalsService.overrideItem : fundamentalsService.resolveItem;
      await fn(item.id, choice, note);
      onDone();
    } catch (e) { setErr(e?.response?.data?.detail || 'Could not record the decision.'); } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="space-y-2">
        {shown.map((o) => (
          <label key={o.index} className="flex min-h-[44px] cursor-pointer items-start gap-2 py-1 text-sm text-gray-800 dark:text-gray-200 sm:min-h-0 sm:py-0">
            <input type="radio" name={`q${item.id}-${mode}`} className="mt-1 h-4 w-4 accent-brand-gold" checked={choice === o.index} onChange={() => setChoice(o.index)} />
            <span className="min-w-0 [overflow-wrap:anywhere]">
              <span className="font-medium">{pn(o.title)}</span>
              {o.summary && <span className="block text-xs text-gray-600 dark:text-gray-300">{pn(o.summary)}</span>}
              <CodeChecks checks={o.checks} />
            </span>
          </label>
        ))}
      </div>
      {hidden > 0 && (
        <button onClick={() => setAll(!all)} className="mt-1 text-xs text-gray-500 underline">
          {all ? 'Hide the unlikely lines' : `Show ${hidden} more earlier line(s) the code also offered — none shares a value or a word with this row`}</button>
      )}
      <textarea value={note} rows={2}
        onChange={(e) => {
          // pasting your AI's answer ("AMD-299: option 0 …") pre-selects that option — you still confirm
          const t = e.target.value;
          setNote(t);
          const m = t.match(/(?:^|\b)(?:[A-Z]+-\d+\s*:\s*)?option\s*(\d+)/i);
          if (m && choice == null) {
            const n = Number(m[1]);
            if (opts.some((o) => o.index === n)) setChoice(n);
          }
        }}
        placeholder="Why — or paste your AI's answer (it starts with the question number and the option)"
        className="mt-2 w-full rounded-lg border border-gray-300 bg-transparent px-3 py-1.5 text-sm dark:border-gray-700" />
      <div className="mt-2 flex items-center gap-3">
        <button disabled={choice == null || busy} onClick={go}
          className="min-h-[44px] rounded-lg bg-brand-gold px-3 py-1.5 text-sm text-white disabled:opacity-40 dark:text-black sm:min-h-0">
          {busy ? 'Recording…' : mode === 'override' ? 'Change the decision' : 'Record decision'}</button>
        {err && <span className="text-xs text-red-600 dark:text-red-400">{err}</span>}
      </div>
    </div>
  );
}

function QuestionCard({ item: listed, onResolved, focused = false }) {
  const [picking, setPicking] = useState(false);
  const box = useRef(null);
  // the queue's / the grid's deep link (?q=): the question's TOP — its title and, on a focused card, the decision right
  // under it — lands under the top bar (`scroll-mt-16`). A card is several screens tall: centring it (as before) stopped
  // inside the answers list (FITB at 1440: y = 3428). Content above it that is still laying out (other cards, briefs,
  // the section opening) moves it, so the scroll is repeated while the page's height changes — until the reader
  // touches, scrolls or types, or 4 s pass.
  useEffect(() => {
    if (!focused || !box.current) return undefined;
    let done = false;
    const go = () => { if (!done && box.current) box.current.scrollIntoView({ block: 'start' }); };
    const stop = () => { done = true; };
    go();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(go) : null;
    if (ro) ro.observe(document.body);
    const evs = ['wheel', 'touchstart', 'keydown', 'mousedown'];
    evs.forEach((e) => window.addEventListener(e, stop, { passive: true }));
    const t = setTimeout(stop, 4000);
    return () => {
      done = true; clearTimeout(t); if (ro) ro.disconnect();
      evs.forEach((e) => window.removeEventListener(e, stop));
    };
  }, [focused]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // a decided question arrives without its brief (it may read the filing); fetched when asked
  const [full, setFull] = useState(null);
  const [loadingBrief, setLoadingBrief] = useState(false);
  const item = full || listed;
  const showBrief = () => {
    setLoadingBrief(true);
    fundamentalsService.getItem(listed.id).then((r) => setFull(r.data.item))
      .catch(() => setErr('Could not load the brief.')).finally(() => setLoadingBrief(false));
  };
  const k = KIND[item.kind] || { label: item.kind, cls: '' };
  const st = STAGE[item.stage] || { label: item.stage, cls: '' };
  const filing = item.subject?.filing || item.evidence?.filing;   // a standard-line question names it in evidence
  const res = item.resolution || {};
  const answers = item.answers || [];
  const claude = answers.filter((a) => a.answerer.startsWith('claude:'));
  const numberedClaude = numbered(claude);
  const mine = answers.filter((a) => a.answerer.startsWith('owner:'));
  const agreed = agreedOption(item);
  const act = async (fn) => {
    setBusy(true); setErr('');
    try { await fn(); onResolved(); } catch (e) { setErr(e?.response?.data?.detail || 'Could not record it.'); }
    finally { setBusy(false); }
  };

  let outcome;
  if (item.stage === 'applied_by_pipeline' && res.policy === 'only_option') {
    outcome = <span className="text-green-700 dark:text-green-400">Settled by code: only one option was possible, so no
      answerer was needed.</span>;
  } else if (item.stage === 'applied_by_pipeline') {
    outcome = <span className="text-green-700 dark:text-green-400">Applied by the pipeline: both blind answers chose
      “{optionText(res.option)}” and passed the code checks.{res.audit_sample ? ' Picked for your audit.' : ''}
      {res.confirmed_by ? ` You confirmed it ${String(res.confirmed_at || '').slice(0, 10)}.` : ''}</span>;
  } else if (item.stage === 'answered_by_you') {
    outcome = <span className="text-sky-700 dark:text-sky-300">Decided by {res.user || 'you'}: “{optionText(res.option)}”
      {res.override ? ' (you changed an earlier decision)' : ''}.</span>;
  } else if (item.stage === 'needs_you') {
    const inv = item.investigator;
    const rec = inv && typeof inv === 'object'
      ? (inv.none_fits ? <>none of the options — {inv.none_fits.answer}</>
        : inv.option_index != null ? optionText(item.options?.[inv.option_index]) : 'no recommendation (it abstained)')
      : null;
    outcome = <span className="text-amber-700 dark:text-amber-300">Needs you — {item.why}.
      {rec && <span className="block text-sky-700 dark:text-sky-300">The investigator recommends: {rec}
        {inv.verified ? ' (every number it cites checked)' : ' (one of its checks failed — read it with care)'}.</span>}
      {inv === 'pending' && <span className="block text-gray-500">The investigator has not looked at it yet.</span>}</span>;
  } else {
    outcome = <span className="text-gray-600 dark:text-gray-300">Waiting — {item.why ? `${item.why}.` : claude.length
      ? `${claude.length} answer(s) so far; two blind answers that pass the checks and agree are needed.` : 'no answers yet.'}
      {' '}You can also decide now.</span>;
  }

  const outcomeStep = (
    <Step n="3" title="Outcome"><div className="text-sm">{outcome}</div>
      {(res.history || []).length > 0 && (
        <div className="mt-1 text-xs text-gray-500">Earlier: {res.history.map((h) =>
          `${h.by || 'owner'} chose ${h.choice} ${String(h.at || '').slice(0, 10)} (changed by ${h.reopened_by})`).join(' · ')}</div>
      )}
    </Step>
  );
  const actionStep = (
    <Step n="4" title="Your action">
      {mine.length > 0 && <div className="mb-2 space-y-2">{mine.map((a) => <AnswerRow key={a.id} a={a} />)}</div>}
      {item.stage === 'needs_you' && agreed != null && !picking && (
        <div className="flex flex-wrap items-center gap-2">
          <button disabled={busy} onClick={() => act(() => fundamentalsService.resolveItem(item.id, agreed, 'approved the agreed answer'))}
            className="min-h-[44px] rounded-lg bg-brand-gold px-3 py-1.5 text-sm text-white disabled:opacity-40 dark:text-black sm:min-h-0">
            Approve: {pn(item.brief?.options?.find((o) => o.index === agreed)?.title) || optionText(item.options[agreed])}</button>
          <button onClick={() => setPicking(true)} className="min-h-[44px] px-1 text-sm text-gray-600 underline dark:text-gray-300 sm:min-h-0">Choose differently</button>
        </div>
      )}
      {item.status === 'open' && (agreed == null || item.stage !== 'needs_you' || picking) && (
        <OptionPicker item={item} onDone={onResolved} mode="resolve" />
      )}
      {item.status === 'resolved' && !picking && (
        <div className="flex flex-wrap items-center gap-2">
          {item.stage === 'applied_by_pipeline' && !res.confirmed_by && (
            <button disabled={busy} onClick={() => act(() => fundamentalsService.confirmItem(item.id, ''))}
              className="min-h-[44px] rounded-lg border border-green-600 px-3 py-1.5 text-sm text-green-700 dark:text-green-400 sm:min-h-0">Looks right</button>
          )}
          <button onClick={() => setPicking(true)} className="min-h-[44px] rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-700 sm:min-h-0">Change…</button>
        </div>
      )}
      {item.status === 'resolved' && picking && <OptionPicker item={item} onDone={onResolved} mode="override" />}
      {err && <div className="mt-1 text-xs text-red-600 dark:text-red-400">{err}</div>}
    </Step>
  );

  return (
    <div ref={box} id={`review-q-${item.id}`}
      className={`min-w-0 scroll-mt-16 rounded-xl border p-3 sm:p-4 ${focused ? 'border-brand-gold ring-2 ring-brand-gold/50' : 'border-gray-200 dark:border-gray-800'}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-[11px] uppercase tracking-wide ${st.cls}`}>{st.label}</span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] uppercase tracking-wide ${k.cls}`}>{k.label}</span>
        {item.subject?.family && <span className="text-xs text-gray-500">{FAMILY_LABEL[item.subject.family]}
          {' · '}{item.subject.gran === 'annual' ? '10-K' : '10-Q'}</span>}
        {filing && <a href={edgarUrl(filing)} target="_blank" rel="noreferrer noopener"
          className="break-all text-xs text-brand-gold underline-offset-2 hover:underline">{filing}</a>}
      </div>
      {focused && (
        // opened from a link (?q=): the question in one line, then what it needs and the decision — all in the first
        // screen; the full brief and the answers follow (steps 1–2)
        <div className="mt-2">
          <div className="break-words font-serif text-base text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">
            {pn(item.brief?.question || item.title)}</div>
          {outcomeStep}
          {actionStep}
        </div>
      )}
      <Step n="1" title="The question, and what the code checked">
        {item.brief ? <BriefView b={item.brief} /> : <div className="break-words text-sm text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">{pn(item.title)}</div>}
        {!item.brief && item.brief_lazy && (
          <button onClick={showBrief} disabled={loadingBrief}
            className="mt-1 text-xs text-brand-gold underline-offset-2 hover:underline disabled:opacity-40">
            {loadingBrief ? 'Loading the brief…' : 'Show the brief'}</button>
        )}
        {item.brief_text && <div className="mt-2"><CopyBrief text={item.brief_text} /></div>}
      </Step>
      <Step n="2" title={`Independent answers (${claude.length}) — each blind, each checked by code`}>
        {claude.length ? (
          <>
            <Agreement list={numberedClaude} />
            <div className="space-y-2">{numberedClaude.map(({ a, label }) => <AnswerRow key={a.id} a={a} label={label} />)}</div>
          </>
        ) : <div className="text-xs text-gray-500">None yet.</div>}
      </Step>
      {!focused && outcomeStep}
      {!focused && actionStep}
    </div>
  );
}

// the 1-in-10 pipeline decisions picked for your check and not yet confirmed or changed by you
const toAudit = (it) => it.stage === 'applied_by_pipeline' && it.resolution?.audit_sample && !it.resolution?.confirmed_by;

const byModels = (it) => it.stage === 'applied_by_pipeline' && it.resolution?.policy === 'two_blind_agree';

function Section({ title, count, sub, open, onToggle, tone = '', children }) {
  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800">
      <button type="button" onClick={onToggle} aria-expanded={open}
        className="flex w-full flex-wrap items-center gap-2 px-4 py-3 text-left">
        <span className="w-3 text-gray-400">{open ? '▾' : '▸'}</span>
        <span className="font-serif text-base text-gray-900 dark:text-gray-100">{title}</span>
        {count != null && <span className={`rounded-full px-2 py-0.5 font-mono text-[11px] tabular-nums ${tone
          || 'bg-gray-500/10 text-gray-600 dark:text-gray-300'}`}>{count}</span>}
        {sub && <span className="w-full pl-5 text-xs text-gray-500 dark:text-gray-400 sm:ml-auto sm:w-auto sm:pl-0">{sub}</span>}
      </button>
      {open && <div className="border-t border-gray-200 px-4 py-3 dark:border-gray-800">{children}</div>}
    </section>
  );
}

function Cards({ rows, onResolved, empty, focusId }) {
  if (!rows.length) return <div className="text-sm text-gray-500">{empty}</div>;
  return <div className="space-y-3">{rows.map((it) => (
    <QuestionCard key={it.id} item={it} onResolved={onResolved} focused={String(it.id) === String(focusId)} />))}</div>;
}

// the questions in the pipeline's own order: what needs you (open), what waits for the answerers, what is settled
function QuestionGroups({ items, onResolved, focusId }) {
  const [open, setOpen] = useState({ needs_you: true, waiting: false, settled: false });
  const [settledBy, setSettledBy] = useState('all');
  const flip = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  // a deep-linked question (?q=) opens the section that holds it, whichever stage it reached since the link was made
  useEffect(() => {
    if (!focusId) return;
    const it = items.find((x) => String(x.id) === String(focusId));
    if (!it) return;
    const g = it.stage === 'needs_you' ? 'needs_you' : it.stage === 'waiting' ? 'waiting' : 'settled';
    setOpen((o) => (o[g] ? o : { ...o, [g]: true }));
    if (g === 'settled') setSettledBy('all');
  }, [items, focusId]);
  const needs = items.filter((it) => it.stage === 'needs_you').sort((x, y) => x.id - y.id);
  const waiting = items.filter((it) => it.stage === 'waiting').sort((x, y) => x.id - y.id);
  const settled = items.filter((it) => it.stage === 'applied_by_pipeline' || it.stage === 'answered_by_you');
  const code = settled.filter((it) => it.stage === 'applied_by_pipeline' && !byModels(it));
  const models = settled.filter(byModels);
  const you = settled.filter((it) => it.stage === 'answered_by_you');
  const audit = settled.filter(toAudit);
  const shownSettled = (settledBy === 'code' ? code : settledBy === 'models' ? models : settledBy === 'you' ? you
    : settledBy === 'audit' ? audit : settled).slice().sort((x, y) => Number(toAudit(y)) - Number(toAudit(x)) || x.id - y.id);
  return (
    <div className="space-y-3">
      <Section title="Needs you" count={needs.length} open={open.needs_you} onToggle={() => flip('needs_you')}
        tone={needs.length ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300' : ''}
        sub="the pipeline could not settle these — your choice applies at the next rebuild">
        <Cards rows={needs} onResolved={onResolved} empty="Nothing needs you." focusId={focusId} />
      </Section>
      <Section title="Waiting for the answerer" count={waiting.length} open={open.waiting} onToggle={() => flip('waiting')}
        sub="two blind Claude answers that agree and pass the code checks settle these — you may decide now">
        <Cards rows={waiting} onResolved={onResolved} empty="Nothing waiting." focusId={focusId} />
      </Section>
      <Section title="Settled" count={settled.length} open={open.settled} onToggle={() => flip('settled')}
        sub={`by code ${code.length} · by two models ${models.length} · by you ${you.length}${audit.length ? ` · ${audit.length} picked for your audit` : ''}`}>
        <div className="space-y-3">
          <Pills value={settledBy} onChange={setSettledBy} items={[{ key: 'all', label: `All ${settled.length}` },
            ...(audit.length ? [{ key: 'audit', label: `To audit ${audit.length}` }] : []),
            { key: 'code', label: `By code ${code.length}` }, { key: 'models', label: `By two models ${models.length}` },
            { key: 'you', label: `By you ${you.length}` }]} />
          <div className="text-xs text-gray-500 dark:text-gray-400">Every decision keeps its answers and evidence.
            “Looks right” records your audit and changes nothing; “Change…” replaces the decision (the old one is kept
            in its history). Decisions apply at the next rebuild.</div>
          <Cards rows={shownSettled} onResolved={onResolved} empty="None." focusId={focusId} />
        </div>
      </Section>
    </div>
  );
}

// ── Line map ─────────────────────────────────────────────────────────────────────────────────────────────────
function evidenceText(e) {
  const ev = e.evidence || {};
  if (ev.split_from) {
    const f = (ev.failing || [])[0];
    return f ? `${f.period}: ${fmt(f.old)} → ${fmt(f.new)}m (${f.class})` : 'split';
  }
  if (ev.corroborator) return `same value on ${(ev.shared_periods || []).length} period(s), ${ev.corroborator.replace(/_/g, ' ')}`;
  if (ev.merged_from) return `= ${ev.merged_from.length} earlier lines`;
  return '';
}

const SPLIT_CHIP = {
  proven: { label: 'explained', cls: 'bg-green-500/10 text-green-700 dark:text-green-300' },
  one_period: { label: 'one year', cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-300' },
  ambiguous: { label: 'ambiguous', cls: 'bg-red-500/10 text-red-700 dark:text-red-300' },
  none: { label: 'unexplained', cls: 'bg-red-500/10 text-red-700 dark:text-red-300' },
};

function LineMap({ data, onOpenLine }) {
  const [show, setShow] = useState('all');
  const all = data?.events || [];
  const splits = all.filter((e) => e.kind === 'split');
  const n = (st) => splits.filter((e) => e.explained === st).length;
  const filters = [{ key: 'all', label: `All ${all.length}` },
    { key: 'split', label: `Splits ${splits.length}` },
    { key: 'split:proven', label: `Explained ${n('proven')}` },
    { key: 'split:one_period', label: `One-year candidates ${n('one_period')}` },
    { key: 'split:open', label: `Unexplained ${n('none') + n('ambiguous')}` },
    { key: 'rename', label: `Renamed ${all.filter((e) => e.kind === 'rename').length}` },
    { key: 'new', label: `New ${all.filter((e) => e.kind === 'new').length}` }];
  const rows = all.filter((e) => show === 'all' || (show === 'split:open'
    ? e.kind === 'split' && ['none', 'ambiguous', ''].includes(e.explained)
    : show.includes(':') ? e.kind === 'split' && e.explained === show.split(':')[1] : e.kind === show));
  const cols = useMemo(() => [
    { key: 'kind', type: 'string', get: (r) => r.kind },
    { key: 'family', type: 'string', get: (r) => r.family },
    { key: 'gran', type: 'string', get: (r) => r.granularity },
    { key: 'line', type: 'string', get: (r) => r.label || r.line_id },
    { key: 'date', type: 'string', get: (r) => r.report_date },
  ], []);
  const { sorted, toggle, indicator } = useSortableRows(rows, cols);
  return (
    <div className="space-y-3">
      <div className="text-sm text-gray-700 dark:text-gray-300">
        How each row continued from filing to filing — renames, splits and new rows the code recorded
        ({`${all.length} across ${data?.lines ?? '…'} lines`}). Nothing to do here unless you want to audit; a split
        the code could not explain is asked as a question above.</div>
      <Pills items={filters} value={show} onChange={setShow} />
      <div className="scrollbar-thin overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wide text-gray-500">
            <tr>
              <SortableTh label="Event" colKey="kind" toggle={toggle} indicator={indicator} />
              <SortableTh label="Statement" colKey="family" toggle={toggle} indicator={indicator} />
              <SortableTh label="Filings" colKey="gran" toggle={toggle} indicator={indicator} />
              <SortableTh label="Line" colKey="line" toggle={toggle} indicator={indicator} />
              <SortableTh label="From" colKey="date" toggle={toggle} indicator={indicator} />
              <th className="px-3 py-2">Evidence</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((e, i) => (
              <tr key={i} className="border-t border-gray-100 dark:border-gray-800/70">
                <td className="whitespace-nowrap px-3 py-2">{EVENT[e.kind] || e.kind}</td>
                <td className="whitespace-nowrap px-3 py-2">{FAMILY_LABEL[e.family] || e.family}</td>
                <td className="whitespace-nowrap px-3 py-2">{e.granularity === 'annual' ? '10-K' : '10-Q'}</td>
                <td className="px-3 py-2">
                  <button className="text-left text-brand-gold hover:underline" title={e.concept}
                    onClick={() => onOpenLine(e.granularity, e.family, e.line_id)}>{e.label || e.concept.split(':').pop()}</button>
                </td>
                <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                  <a href={edgarUrl(e.filing)} target="_blank" rel="noreferrer noopener" className="hover:underline">{e.report_date}</a></td>
                <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                  {e.kind === 'split' ? (
                    <>
                      <span className={`mr-1.5 rounded-full px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${SPLIT_CHIP[e.explained]?.cls || SPLIT_CHIP.none.cls}`}>
                        {SPLIT_CHIP[e.explained]?.label || SPLIT_CHIP.none.label}</span>
                      {e.why}
                    </>
                  ) : e.kind === 'rename' && e.why ? (
                    <>
                      <span className={`mr-1.5 rounded-full px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${e.same_label
                        ? SPLIT_CHIP.proven.cls : SPLIT_CHIP.one_period.cls}`}>{e.same_label ? 'same name' : 'name changed'}</span>
                      {e.why} · {evidenceText(e)}
                    </>
                  ) : evidenceText(e)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Usage (the ledger: what the Claude answerers used, per pipeline) ─────────────────────────────────────────
function tokensText(t) {
  const n = (t?.input_tokens || 0) + (t?.output_tokens || 0);
  return n >= 1e6 ? `${(n / 1e6).toFixed(2)}M tokens` : `${Math.round(n / 1000)}k tokens`;
}

const PIPELINE = { unbound_node: 'New lines', split_reason: 'Why a line split', rename_check: 'Renamed rows',
  context_suspect: 'Other period’s value', sign_flip: 'Sign flip', version_material: 'Changed value' };

function Usage({ u }) {
  if (!u) return <div className="text-sm text-gray-500">Loading…</div>;
  const p = u.pairing || {};
  return (
    <div className="space-y-3">
      <div className="text-sm text-gray-700 dark:text-gray-300">
        Every Claude answer records what it used, from Claude Code itself. It all runs on your subscription — nothing is
        billed. The dollar column is only Claude Code's API-price equivalent of those tokens, a yardstick for size; your
        real cost is a share of the plan's usage limits (claude.ai → Settings → Usage). Answers given before the ledger
        existed are counted as unmetered.</div>
      <div className="scrollbar-thin overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
        <table className="w-full min-w-[560px] text-sm tabular-nums">
          <thead className="text-left text-[11px] uppercase tracking-wide text-gray-500">
            <tr><th className="px-3 py-2">Pipeline</th><th className="px-3 py-2 text-right">Questions</th>
              <th className="px-3 py-2 text-right">Answers</th><th className="px-3 py-2 text-right">Input</th>
              <th className="px-3 py-2 text-right">Output</th><th className="px-3 py-2 text-right">API-price equiv.</th>
              <th className="px-3 py-2 text-right">Unmetered</th></tr>
          </thead>
          <tbody>
            {(u.rows || []).map((r) => (
              <tr key={r.pipeline} className="border-t border-gray-100 dark:border-gray-800/70">
                <td className="px-3 py-2">{PIPELINE[r.pipeline] || r.pipeline}</td>
                <td className="px-3 py-2 text-right">{r.questions || 0}</td>
                <td className="px-3 py-2 text-right">{r.answers || 0}</td>
                <td className="px-3 py-2 text-right">{Math.round((r.input_tokens || 0) / 1000)}k</td>
                <td className="px-3 py-2 text-right">{Math.round((r.output_tokens || 0) / 1000)}k</td>
                <td className="px-3 py-2 text-right">${((r.est_usd_x100 || 0) / 100).toFixed(2)}</td>
                <td className="px-3 py-2 text-right text-gray-500">{r.unmetered_answers || 0}</td>
              </tr>
            ))}
            <tr className="border-t border-gray-200 font-medium dark:border-gray-700">
              <td className="px-3 py-2">Total</td><td className="px-3 py-2 text-right">{u.total?.questions || 0}</td>
              <td className="px-3 py-2 text-right">{u.total?.answers || 0}</td>
              <td className="px-3 py-2 text-right">{Math.round((u.total?.input_tokens || 0) / 1000)}k</td>
              <td className="px-3 py-2 text-right">{Math.round((u.total?.output_tokens || 0) / 1000)}k</td>
              <td className="px-3 py-2 text-right">${((u.total?.est_usd_x100 || 0) / 100).toFixed(2)}</td>
              <td className="px-3 py-2 text-right text-gray-500">{u.total?.unmetered_answers || 0}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="rounded-xl border border-gray-200 p-3 text-sm dark:border-gray-800">
        <div className="text-[11px] uppercase tracking-wide text-gray-500">Two models or one?</div>
        <div className="mt-1 text-gray-800 dark:text-gray-200">{p.verdict}</div>
        <div className="mt-1 text-xs text-gray-500">{p.comparable_questions || 0} questions where both answers passed the
          code checks · {p.agreed || 0} agreed · {p.disagreed || 0} disagreed · the second model was right
          {' '}{p.second_model_was_right || 0} time(s). After 200 with ≤ 1% disagreement and none caught, switch to one
          model asked two ways.</div>
      </div>
    </div>
  );
}

// ── Statements ───────────────────────────────────────────────────────────────────────────────────────────────
function Statements({ ticker, focus }) {
  const [gran, setGran] = useState(focus?.gran || 'annual');
  const [fam, setFam] = useState(focus?.family || 'IS_CI');
  // the CURRENT MODEL (one row per printed row, each period at its latest figure) unless a question points at an
  // as-filed line
  const [view, setView] = useState(focus ? 'filed' : 'model');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => { if (focus) { setGran(focus.gran); setFam(focus.family); setView('filed'); } }, [focus]);
  useEffect(() => {
    setData(null); setErr('');
    fundamentalsService.getLines(ticker, gran, fam, view).then((r) => setData(r.data))
      .catch(() => setErr('Could not load the statement.'));
  }, [ticker, gran, fam, view]);
  const periods = data?.periods || [];
  const shownPeriods = gran === 'annual' ? periods : periods.slice(-12);
  const cols = useMemo(() => [
    { key: 'label', type: 'string', get: (r) => r.label },
    ...shownPeriods.map((p) => ({ key: p.key, type: 'number',
      get: (r) => (r.cells[p.key]?.value == null ? null : Number(r.cells[p.key].value)) })),
  ], [shownPeriods]);
  const { sorted, toggle, indicator } = useSortableRows(data?.lines || [], cols);
  // FY of a period = its own DEI label, else the year it ends in (comparative years carry no label of their own)
  const yy = (p) => String(p.fy || p.end.slice(0, 4)).slice(2);
  const head = (p) => (p.instant ? p.end : p.kind === 'FY' ? `FY${yy(p)}`
    : `${p.fp && p.fp !== '3M' ? p.fp : 'Q'} FY${yy(p)}`);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        <Pills items={[{ key: 'annual', label: 'Annual (10-K)' }, { key: 'interim', label: 'Quarterly (10-Q)' }]}
          value={gran} onChange={setGran} />
        <Pills items={FAMILIES} value={fam} onChange={setFam} />
        <Pills items={[{ key: 'model', label: 'Current model' }, { key: 'filed', label: 'As-filed lines' }]}
          value={view} onChange={setView} />
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400">$ millions except per share · as known now · italic =
        derived (Q4 = FY − 9M, cash-flow quarters from year-to-date) · amber = held for review · — with a reason =
        not derivable{view === 'model' && <> · <span className="underline decoration-dotted">dotted</span> = restated
        (hover: first reported) · one row per printed row, each period at its latest figure</>}</div>
      {err && <div className="text-sm text-red-600">{err}</div>}
      {!data && !err && <div className="text-sm text-gray-500">Loading…</div>}
      {data && (
        <div className="scrollbar-thin overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
          <table className="text-sm">
            <thead className="text-[11px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="sticky left-0 z-10 bg-white px-3 py-2 text-left dark:bg-gray-950">
                  <button onClick={() => toggle('label')}>Line{indicator('label')}</button></th>
                {shownPeriods.map((p) => (
                  <th key={p.key} className="whitespace-nowrap px-3 py-2 text-right">
                    <button onClick={() => toggle(p.key)}>{head(p)}{indicator(p.key)}</button></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((ln) => (
                <tr key={ln.row || ln.line_id}
                  className={`border-t border-gray-100 dark:border-gray-800/70 ${focus?.line_id === ln.line_id ? 'bg-brand-gold/10' : ''}`}>
                  <td className="sticky left-0 z-10 max-w-[220px] bg-white px-3 py-1.5 text-gray-800 dark:bg-gray-950 dark:text-gray-200"
                    title={ln.line_id}>{ln.bridge && (
                      <span className="mr-1.5 rounded-full bg-sky-500/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-sky-700 dark:text-sky-300">bridge</span>)}
                    {ln.line_id.includes('|cmp~') && (
                      <span className="mr-1.5 rounded-full bg-sky-500/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-sky-700 dark:text-sky-300">comparable</span>)}
                    {ln.label}
                    {ln.note && <span className="block text-[11px] font-normal text-gray-500 dark:text-gray-400">{ln.note}</span>}</td>
                  {shownPeriods.map((p) => {
                    const c = ln.cells[p.key];
                    if (!c) return <td key={p.key} className="px-3 py-1.5" />;
                    // as served, like the Folio grid (the row's display sign is not applied)
                    const v = c.value == null ? null : Number(c.value);
                    const derived = c.kind && c.kind !== 'direct' && c.kind !== 'fold';
                    const first = c.first == null ? null : Number(c.first);
                    const tip = c.fold ? `Folded into another row by ${c.fold.filing}`
                      : [NULL_REASON[c.null_reason] || c.null_reason, fillNote(c, ln.unit), first != null && `restated — first reported ${fmt(first, ln.unit)}`, (c.accessions || []).join(', ')].filter(Boolean).join(' · ');
                    return (
                      <td key={p.key} title={tip}
                        className={`whitespace-nowrap px-3 py-1.5 text-right tabular-nums ${derived ? 'italic' : ''}
                          ${first != null ? 'underline decoration-dotted' : ''}
                          ${c.fill && c.kind !== 'bridge' ? 'bg-sky-500/10' : ''}
                          ${c.pending ? 'bg-amber-500/10' : ''} text-gray-800 dark:text-gray-200`}>
                        {c.value == null ? <span className="text-gray-500 dark:text-gray-400" title={NULL_REASON[c.null_reason] || c.null_reason}>
                          —</span> : fmt(v, ln.unit)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────────────────────────────────────
export default function FundamentalsReviewPage({ ticker: tickerProp, onChanged, focusId = '' }) {
  // the company comes from Folio's picker (one shared component, ?ticker= in the address)
  const ticker = (tickerProp || (() => {
    try { return new URLSearchParams(window.location.search).get('ticker') || 'MU'; } catch { return 'MU'; }
  })()).toUpperCase();
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [lineMap, setLineMap] = useState(null);
  const [focus, setFocus] = useState(null);
  const [more, setMore] = useState({ statements: false, usage: false, history: false });
  const [err, setErr] = useState('');
  const lastActive = useRef(Date.now());

  const load = useCallback(() => {
    setErr('');
    Promise.all([fundamentalsService.getSummary(ticker), fundamentalsService.getItems(ticker, 'all'),
      fundamentalsService.getLineMap(ticker)])
      .then(([s, i, m]) => { setSummary(s.data); setItems(i.data.items); setLineMap(m.data); })
      .catch(() => setErr('Could not load the review page.'));
  }, [ticker]);
  useEffect(() => { setSummary(null); setItems([]); setLineMap(null); setFocus(null); load(); }, [load]);

  // active review time: a beat per minute while the tab is visible and the owner touched it in the last 2 minutes
  useEffect(() => {
    const mark = () => { lastActive.current = Date.now(); };
    ['click', 'keydown', 'scroll', 'touchstart'].forEach((ev) => window.addEventListener(ev, mark, { passive: true }));
    const t = setInterval(() => {
      if (document.visibilityState === 'visible' && Date.now() - lastActive.current < 120000) {
        fundamentalsService.heartbeat(60).catch(() => {});
      }
    }, 60000);
    return () => {
      clearInterval(t);
      ['click', 'keydown', 'scroll', 'touchstart'].forEach((ev) => window.removeEventListener(ev, mark));
    };
  }, []);

  const open = items.filter((x) => x.status === 'open').length;
  const needsYou = items.filter((x) => x.stage === 'needs_you').length;
  const resolved = items.filter((x) => x.status === 'resolved').length;
  const onResolved = () => { load(); if (onChanged) onChanged(); };
  const openLine = (gran, family, lineId) => {
    setFocus({ gran, family, line_id: lineId });
    setMore((m) => ({ ...m, statements: true }));
  };
  const flip = (k) => setMore((m) => ({ ...m, [k]: !m[k] }));

  return (
    <div className="space-y-4">
      {err && <div className="text-sm text-red-600">{err}</div>}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Tile label="Need you" value={summary ? needsYou : '…'} sub={summary ? `${open} open` : ''}
          valueCls={needsYou ? 'text-amber-600 dark:text-amber-400' : ''} />
        <Tile label="Settled" value={summary ? resolved : '…'} />
        <Tile label="Your review time" value={summary ? `${summary.review_minutes} min` : '…'}
          sub={summary?.last_rebuild ? `rebuilt ${summary.last_rebuild.slice(0, 10)}` : ''} />
        <Tile label="Claude usage" value={summary?.usage ? tokensText(summary.usage.total) : '…'}
          sub={summary?.usage ? `API-price equivalent ≈ $${((summary.usage.total.est_usd_x100 || 0) / 100).toFixed(2)} · not billed` : ''}
          onClick={() => flip('usage')} />
      </div>
      {summary ? <QuestionGroups items={items} onResolved={onResolved} focusId={focusId} /> : !err && <div className="text-sm text-gray-500">Loading…</div>}
      <Section title="Statements — as filed" open={more.statements} onToggle={() => flip('statements')}
        sub="the company's own lines as the pipeline serves them, or each as-filed line">
        <Statements ticker={ticker} focus={focus} />
      </Section>
      <Section title="Answerers' usage" open={more.usage} onToggle={() => flip('usage')}
        sub="what the blind Claude answers used, per pipeline">
        <Usage u={summary?.usage} />
      </Section>
      <Section title="History — how rows continued across filings" count={lineMap ? (lineMap.events || []).length : null}
        open={more.history} onToggle={() => flip('history')}
        sub="row renames and splits the code recorded — nothing to do unless you want to audit">
        <LineMap data={lineMap} onOpenLine={openLine} />
      </Section>
    </div>
  );
}
